import React, { useEffect, useMemo, useState } from 'react'
import { Button, Icon, Input, Layout } from 'antd'
import { Card, PageHeader, Table, Typography, message } from 'antd-v4'
import { browserHistory } from 'react-router'
import { parseErrorMsg } from '../../../utilities/helpers'
import { createSalesChannel, getSalesChannels, ISalesChannel, updateSalesChannel } from './api'
import { ChannelFormModal } from './ChannelFormModal'

const { Text } = Typography

export const AdminPricingMenuChannels = () => {
  const [channels, setChannels] = useState<ISalesChannel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [selectedChannel, setSelectedChannel] = useState<ISalesChannel | undefined>(undefined)

  const loadChannels = async () => {
    setIsLoading(true)

    try {
      const list = await getSalesChannels()
      setChannels(list)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading sales channels')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadChannels()
  }, [])

  const filteredChannels = useMemo(() => {
    const normalizedSearch = appliedSearch.trim().toLowerCase()
    const normalizeValue = (value?: string) => (value || '').toLowerCase()

    if (!normalizedSearch) {
      return channels
    }

    return channels.filter((channel) => {
      return (
        normalizeValue(channel.name).includes(normalizedSearch) ||
        normalizeValue(channel.id).includes(normalizedSearch) ||
        normalizeValue(channel.environment).includes(normalizedSearch)
      )
    })
  }, [channels, appliedSearch])

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedChannel(undefined)
  }

  const applyFilters = () => {
    setAppliedSearch(search)
  }

  const resetFilters = () => {
    setSearch('')
    setAppliedSearch('')
  }

  const handleSubmit = async (name: string, environment: 'US' | 'GL', active: boolean) => {
    setIsSaving(true)

    try {
      if (selectedChannel) {
        await updateSalesChannel(selectedChannel.id, name, environment, active)
        message.success('Sales channel updated successfully')
      } else {
        await createSalesChannel(name, environment, active)
        message.success('Sales channel created successfully')
      }

      closeModal()
      await loadChannels()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while saving the sales channel')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Layout>
      <Layout.Sider
        className='background-shadow'
        width={280}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          background: '#fff',
          left: 0,
        }}
      >
        <div style={{ padding: 10 }}>
          <div style={{ marginBottom: 8, color: 'rgba(0,0,0,.65)' }}>
            Search by Name, Key or Environment
          </div>

          <Input
            placeholder='Search...'
            value={search}
            prefix={<Icon type='search' />}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={applyFilters}
          />

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type='primary' onClick={applyFilters} loading={isLoading}>
              Apply
            </Button>
            <Button onClick={resetFilters}>Reset</Button>
          </div>
        </div>
      </Layout.Sider>

      <Layout
        style={{ marginLeft: 280, borderLeft: '1px solid #f0f0f0', backgroundColor: 'white' }}
      >
        <div className='p-4'>
          <PageHeader
            style={{ border: '1px solid rgb(235, 237, 240)' }}
            className='mb-4'
            onBack={() => browserHistory.goBack()}
            title='Global Sales Channels'
            subTitle='Manage sales channels available to merchants in pricing menus.'
            extra={[
              <Button key='add-channel' type='primary' onClick={() => setIsModalOpen(true)}>
                Add New Channel
              </Button>,
            ]}
          />

          <Card>
            <Table<ISalesChannel>
              rowKey='id'
              loading={isLoading}
              dataSource={filteredChannels}
              pagination={{ pageSize: 20, showSizeChanger: true }}
            >
              <Table.Column<ISalesChannel> title='Name' dataIndex='name' key='name' />
              <Table.Column<ISalesChannel>
                title='Environment'
                dataIndex='environment'
                key='environment'
              />
              <Table.Column<ISalesChannel>
                title='Status'
                dataIndex='active'
                key='active'
                render={(isActive: boolean) => (isActive ? 'Active' : 'Inactive')}
              />
              <Table.Column<ISalesChannel>
                title='Key'
                dataIndex='id'
                key='id'
                render={(id: string) => <Text code>{id}</Text>}
              />
              <Table.Column<ISalesChannel>
                title='Created At'
                dataIndex='createdAt'
                key='createdAt'
                render={(value?: string) => value || '-'}
              />
              <Table.Column<ISalesChannel>
                title='Actions'
                key='actions'
                width={130}
                render={(_, record) => (
                  <Button
                    onClick={() => {
                      setSelectedChannel(record)
                      setIsModalOpen(true)
                    }}
                  >
                    Edit
                  </Button>
                )}
              />
            </Table>
          </Card>

          <ChannelFormModal
            visible={isModalOpen}
            isSaving={isSaving}
            selectedChannel={selectedChannel}
            onCancel={closeModal}
            onSubmit={handleSubmit}
          />
        </div>
      </Layout>
    </Layout>
  )
}
